import type { QuestionnaireDefinition, QuestionnaireResponses } from './types'

export const QUESTIONNAIRE_DRAFT_PREFIX = 'constituent.questionnaire.v1:'
export const QUESTIONNAIRE_DRAFT_MAX_BYTES = 64 * 1024
export const QUESTIONNAIRE_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000
export const QUESTIONNAIRE_SUBMIT_SIGNAL_KEY = 'constituent.questionnaire.submit.v1'

export type QuestionnaireFlowStep = 'intro' | 'question' | 'review'

export interface QuestionnaireDraft {
  schemaVersion: 1
  definitionVersion: string
  answers: Record<string, number | null>
  dirtyIds: string[]
  skippedIds: string[]
  step: QuestionnaireFlowStep
  questionId: string | null
  savedAt: number
  baselineFingerprint: string
}

export type DraftReadResult =
  | { status: 'none' }
  | { status: 'available'; draft: QuestionnaireDraft }
  | { status: 'expired' | 'invalid' | 'incompatible'; key: string }
  | { status: 'unavailable' }

export function availableSessionStorage(): Storage | null {
  try { return window.sessionStorage } catch { return null }
}

function storageKeyPart(value: string): string {
  return encodeURIComponent(value)
}

export function questionnaireDraftKey(userId: string, definitionVersion: string): string {
  return `${QUESTIONNAIRE_DRAFT_PREFIX}${storageKeyPart(userId)}:${storageKeyPart(definitionVersion)}`
}

export function validQuestionnaireAnswers(
  definition: QuestionnaireDefinition,
  answers: Record<string, unknown> | null | undefined,
): Record<string, number | null> {
  const allowedValues = new Set(definition.answer_options.map((option) => option.value))
  return Object.fromEntries(definition.questions.map((question) => {
    const value = answers?.[question.id]
    return [question.id, typeof value === 'number' && allowedValues.has(value) ? value : null]
  }))
}

export function questionnaireResponseFingerprint(
  definition: QuestionnaireDefinition,
  responses: QuestionnaireResponses,
): string {
  const answers = validQuestionnaireAnswers(definition, responses.answers)
  const canonical = JSON.stringify({
    version: responses.result?.questionnaire_version ?? null,
    completedAt: responses.result?.questionnaire_completed_at ?? null,
    answers: definition.questions.map((question) => [question.id, answers[question.id]]),
  })
  // This is a change detector, not a secret or authenticity proof. Keeping it
  // synchronous lets every answer edit persist synchronously as required.
  let first = 2166136261
  let second = 5381
  for (let index = 0; index < canonical.length; index += 1) {
    first = Math.imul(first ^ canonical.charCodeAt(index), 16777619)
    second = Math.imul(second, 33) ^ canonical.charCodeAt(index)
  }
  return `${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

function isDraft(value: unknown, definition: QuestionnaireDefinition): value is QuestionnaireDraft {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const draft = value as Partial<QuestionnaireDraft>
  if (draft.schemaVersion !== 1 || draft.definitionVersion !== definition.version
    || !Number.isFinite(draft.savedAt) || typeof draft.baselineFingerprint !== 'string'
    || !['intro', 'question', 'review'].includes(draft.step ?? '')
    || !draft.answers || typeof draft.answers !== 'object' || Array.isArray(draft.answers)
    || !Array.isArray(draft.dirtyIds) || !Array.isArray(draft.skippedIds)) return false
  const ids = new Set(definition.questions.map((question) => question.id))
  if (draft.questionId !== null && (typeof draft.questionId !== 'string' || !ids.has(draft.questionId))) return false
  if (draft.step === 'question' && draft.questionId === null) return false
  if (draft.dirtyIds.some((id) => typeof id !== 'string' || !ids.has(id))
    || draft.skippedIds.some((id) => typeof id !== 'string' || !ids.has(id))) return false
  const normalized = validQuestionnaireAnswers(definition, draft.answers as Record<string, unknown>)
  return definition.questions.every((question) => normalized[question.id] === (draft.answers as Record<string, unknown>)[question.id])
}

export function readQuestionnaireDraft(
  storage: Storage | null,
  userId: string,
  definition: QuestionnaireDefinition,
  now = Date.now(),
): DraftReadResult {
  const key = questionnaireDraftKey(userId, definition.version)
  if (!storage) return { status: 'unavailable' }
  try {
    const raw = storage.getItem(key)
    if (raw !== null) {
      if (byteLength(raw) > QUESTIONNAIRE_DRAFT_MAX_BYTES) return { status: 'invalid', key }
      let parsed: unknown
      try { parsed = JSON.parse(raw) } catch { return { status: 'invalid', key } }
      if (!isDraft(parsed, definition)) return { status: 'invalid', key }
      if (now - parsed.savedAt > QUESTIONNAIRE_DRAFT_MAX_AGE_MS || parsed.savedAt > now + 60_000) return { status: 'expired', key }
      return { status: 'available', draft: parsed }
    }
    const userPrefix = `${QUESTIONNAIRE_DRAFT_PREFIX}${storageKeyPart(userId)}:`
    for (let index = 0; index < storage.length; index += 1) {
      const candidate = storage.key(index)
      if (candidate?.startsWith(userPrefix)) return { status: 'incompatible', key: candidate }
    }
    return { status: 'none' }
  } catch {
    return { status: 'unavailable' }
  }
}

export function writeQuestionnaireDraft(storage: Storage | null, userId: string, draft: QuestionnaireDraft): boolean {
  if (!storage) return false
  try {
    const serialized = JSON.stringify(draft)
    if (byteLength(serialized) > QUESTIONNAIRE_DRAFT_MAX_BYTES) return false
    storage.setItem(questionnaireDraftKey(userId, draft.definitionVersion), serialized)
    return true
  } catch {
    return false
  }
}

export function removeQuestionnaireDraft(storage: Storage | null, userId: string, definitionVersion: string): void {
  if (!storage) return
  try { storage.removeItem(questionnaireDraftKey(userId, definitionVersion)) } catch { /* privacy mode */ }
}

export function questionnaireDraftSavedAt(storage: Storage | null, userId: string, definitionVersion: string): number | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(questionnaireDraftKey(userId, definitionVersion))
    if (!raw || byteLength(raw) > QUESTIONNAIRE_DRAFT_MAX_BYTES) return null
    const value = JSON.parse(raw) as { savedAt?: unknown }
    return typeof value.savedAt === 'number' && Number.isFinite(value.savedAt) ? value.savedAt : null
  } catch { return null }
}

export function removeQuestionnaireDraftAtOrBefore(
  storage: Storage | null,
  userId: string,
  definitionVersion: string,
  submittedAt: number,
): boolean {
  const savedAt = questionnaireDraftSavedAt(storage, userId, definitionVersion)
  if (savedAt !== null && savedAt > submittedAt) return false
  removeQuestionnaireDraft(storage, userId, definitionVersion)
  return true
}

export function answersMatch(
  definition: QuestionnaireDefinition,
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): boolean {
  const a = validQuestionnaireAnswers(definition, left)
  const b = validQuestionnaireAnswers(definition, right)
  return definition.questions.every((question) => a[question.id] === b[question.id])
}

export function publishQuestionnaireSubmitInvalidation(userId: string, definitionVersion: string): number | null {
  const at = Date.now()
  try {
    localStorage.setItem(QUESTIONNAIRE_SUBMIT_SIGNAL_KEY, JSON.stringify({
      userId,
      definitionVersion,
      at,
      nonce: Math.random().toString(36).slice(2),
    }))
    return at
  } catch { return null /* cross-tab invalidation is best effort; no answers are broadcast */ }
}

export function readQuestionnaireSubmitInvalidation(): { userId: string; definitionVersion: string; at: number } | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(QUESTIONNAIRE_SUBMIT_SIGNAL_KEY) ?? 'null') as Record<string, unknown> | null
    if (!parsed || typeof parsed.userId !== 'string' || typeof parsed.definitionVersion !== 'string' || typeof parsed.at !== 'number') return null
    return { userId: parsed.userId, definitionVersion: parsed.definitionVersion, at: parsed.at }
  } catch { return null }
}
